/**
 * Groupmate mini swatches inside collection cards, as a colour selector.
 *
 * The app's product-card block renders each sibling as
 *   <li class="gm_item gm_item--miniswatch" data-product-id="{colour}">
 *     <div class="gm_swatchwrap" title="{sibling title}"><img src="…&width=60"></div>
 * with no link and no behaviour. Here a swatch works like a variant picker on a product page:
 * clicking it switches the card to that sibling — image (the swatch's own picture, requested at
 * card size), title, price in the theme's own markup, and where the card's links point — and the
 * card stays that way until another swatch is clicked. The image and the title are what navigate.
 * The swatch matching the card's own product comes pre-selected.
 *
 * Siblings are separate products, and the sibling's title is the only identifier the app exposes,
 * so price and URL come from Shopify's Predictive Search API — the endpoint the theme's search
 * drawer already uses — matched on the exact title. Lookups are cached and started on hover/focus,
 * so by the time a swatch is clicked the data is usually already there. Sibling URLs keep the
 * card's collection context (`within: collection`), so the product page keeps its breadcrumb.
 *
 * The theme's hover image (.product-secondary-image, driven by app.js) follows the selection too:
 * first to the sibling's cover, then — once /products/{handle}.js answers — to its real second
 * picture, so hovering the card never shows another product's angle.
 *
 * Progressive enhancement: if the API is unavailable, the click still switches image and title.
 */
(function () {
  const ITEM = '.product-card .gm_item--miniswatch';
  const PILL = '.product-card .gm_more_pill';
  const IMAGE_WIDTHS = [375, 770];

  const lookups = new Map();
  const cards = new WeakMap();
  const productJsons = new Map();

  function titleOf(item) {
    if (item.dataset.siblingTitle) return item.dataset.siblingTitle;
    const wrap = item.querySelector('.gm_swatchwrap');
    return ((wrap && wrap.getAttribute('title')) || '').trim();
  }

  /* ---------- lookup ---------- */

  function lookup(title) {
    if (!title) return Promise.resolve(null);
    if (lookups.has(title)) return lookups.get(title);

    // Sold-out siblings must still resolve: by default the API pushes unavailable products to the
    // end, where a small limit would drop them. 'show' keeps normal ranking.
    const url = `${theme.routes.predictive_search_url}?q=${encodeURIComponent(title)}` +
      `&${encodeURIComponent('resources[type]')}=product` +
      `&${encodeURIComponent('resources[limit]')}=10` +
      `&${encodeURIComponent('resources[options][unavailable_products]')}=show` +
      `&${encodeURIComponent('resources[options][fields]')}=title`;

    const request = fetch(url, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => {
        const products = (json && json.resources && json.resources.results && json.resources.results.products) || [];
        return products.find((product) => product.title.trim().toLowerCase() === title.toLowerCase()) || null;
      })
      .catch(() => null);

    lookups.set(title, request);
    return request;
  }

  /**
   * The sibling's URL in the same collection context as the card's own link
   * (/collections/{handle}/products/{sibling}); falls back to the product's plain URL.
   * The API's URL carries search-attribution parameters, which the product page does not need.
   */
  function siblingUrl(state, product) {
    if (state.titleHref && /\/products\/[^/?#]+/.test(state.titleHref)) {
      return state.titleHref.replace(/\/products\/[^/?#]+/, `/products/${product.handle}`);
    }
    return product.url.split('?')[0];
  }

  /* ---------- price, in the theme's own markup (see snippets/product-price.liquid) ---------- */

  function money(amount) {
    if (typeof formatMoney === 'function' && theme.settings && theme.settings.money_with_currency_format) {
      return formatMoney(Math.round(parseFloat(amount) * 100), theme.settings.money_with_currency_format);
    }
    return amount;
  }

  function priceHtml(product) {
    const price = parseFloat(product.price);
    const compareAt = parseFloat(product.compare_at_price_max || 0);
    const onSale = compareAt > 0 && compareAt !== price;

    let amount = money(product.price_min || product.price);
    if (product.price_min !== product.price_max && theme.strings && theme.strings.fromPriceHtml) {
      amount = theme.strings.fromPriceHtml.replace('[price]', amount);
    }

    return (onSale ? `<del><span class="amount">${money(product.compare_at_price_max)}</span></del>` : '') +
      `<ins><span class="amount${onSale ? ' discounted' : ''}">${amount}</span></ins>`;
  }

  /* ---------- badges (see snippets/product-card-badge.liquid) ---------- */

  // Only the stock/sale badges follow the selection; tag-driven custom badges stay as rendered.
  function updateBadges(card, product) {
    const wrap = card.querySelector('.product-card--badges');
    if (!wrap || !theme.strings) return;
    wrap.querySelectorAll('.badge.out-of-stock, .badge.onsale').forEach((badge) => badge.remove());
    const tags = product.tags || [];
    const onSale = parseFloat(product.compare_at_price_max || 0) > parseFloat(product.price);
    let html = '';
    if (!product.available && !tags.includes('COMING SOON')) {
      html += '<span class="badge out-of-stock">' + theme.strings.badgeSoldOut + '</span>';
    }
    if (onSale && product.available) {
      html += '<span class="badge onsale">' + (tags.includes('exchange-only') ? theme.strings.badgeSaleFinal : theme.strings.badgeSale) + '</span>';
    }
    wrap.insertAdjacentHTML('afterbegin', html);
  }

  /* ---------- selection ---------- */

  // Full product JSON (Ajax Product API) — the only place the sibling's other pictures live.
  function productJson(handle) {
    if (productJsons.has(handle)) return productJsons.get(handle);
    const base = theme.routes.root_url || '/';
    const root = base.endsWith('/') ? base.slice(0, -1) : base;
    const request = fetch(`${root}/products/${handle}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    productJsons.set(handle, request);
    return request;
  }

  function withWidth(url, width) {
    if (/[?&]width=[0-9]+/.test(url)) return url.replace(/([?&])width=[0-9]+/, `$1width=${width}`);
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  // Points a card image at another file at the card's renditions. Images the theme has not
  // unveiled yet (lazysizes, data-src) keep deferring; loaded ones switch right away.
  function setImage(img, url, alt) {
    const srcset = IMAGE_WIDTHS.map((w) => `${withWidth(url, w)} ${w}w`).join(', ');
    img.setAttribute('data-srcset', srcset);
    img.setAttribute('data-src', withWidth(url, IMAGE_WIDTHS[0]));
    if (img.hasAttribute('src')) {
      img.setAttribute('srcset', srcset);
      img.setAttribute('src', withWidth(url, IMAGE_WIDTHS[0]));
    }
    if (alt) img.setAttribute('alt', alt);
  }

  // The card's own product, captured once before anything is switched: it decides which swatch
  // starts selected and provides the collection context for sibling URLs.
  function cardState(card) {
    if (cards.has(card)) return cards.get(card);
    const title = card.querySelector('.product-card-title');
    const state = {
      image: card.querySelector('.product-primary-image'),
      hoverImages: card.querySelectorAll('.product-secondary-image'),
      imageLink: card.querySelector('.product-card--featured-image-link'),
      title,
      titleText: title ? title.textContent.trim() : '',
      titleHref: title ? title.getAttribute('href') : null,
      price: card.querySelector('.product-card-info .price'),
    };
    cards.set(card, state);
    return state;
  }

  function markSelected(card, item) {
    card.querySelectorAll(ITEM).forEach((other) => other.setAttribute('aria-pressed', other === item ? 'true' : 'false'));
  }

  function select(card, item) {
    const state = cardState(card);
    const siblingTitle = titleOf(item);
    card.dataset.selectedSibling = siblingTitle;
    markSelected(card, item);

    // Instant: the swatch already carries the sibling's picture and title.
    const swatchImage = item.querySelector('.gm_swatchwrap img');
    if (state.image && swatchImage && /[?&]width=[0-9]+/.test(swatchImage.src)) {
      setImage(state.image, swatchImage.src, siblingTitle);
      // Until the sibling's other pictures are known, its cover stands in for the hover image —
      // better a repeated angle than the previous product's.
      state.hoverImages.forEach((img) => setImage(img, swatchImage.src, siblingTitle));
    }
    if (state.title && siblingTitle) {
      state.title.textContent = siblingTitle;
      state.title.setAttribute('title', siblingTitle);
    }
    if (state.imageLink && siblingTitle) state.imageLink.setAttribute('title', siblingTitle);

    // From the lookup: price, and where the image and the title now lead.
    lookup(siblingTitle).then((product) => {
      // Only if this swatch is still the selected one by the time the lookup lands.
      if (!product || card.dataset.selectedSibling !== siblingTitle) return;
      const href = siblingUrl(state, product);
      if (state.title) state.title.setAttribute('href', href);
      if (state.imageLink) state.imageLink.setAttribute('href', href);
      if (state.price) state.price.innerHTML = priceHtml(product);
      updateBadges(card, product);

      if (!state.hoverImages.length) return;
      productJson(product.handle).then((json) => {
        if (!json || card.dataset.selectedSibling !== siblingTitle) return;
        const others = (json.images || []).slice(1);
        if (!others.length) return;
        state.hoverImages.forEach((img, i) => setImage(img, others[i] || others[others.length - 1], siblingTitle));
      });
    });
  }

  /* ---------- wiring ---------- */

  function enhance(item) {
    if (item.dataset.siblingEnhanced) return;
    item.dataset.siblingEnhanced = 'true';
    const title = titleOf(item);
    const card = item.closest('.product-card');
    const state = card ? cardState(card) : null;

    item.setAttribute('role', 'button');
    item.setAttribute('tabindex', '0');
    // The swatch for the product the card already shows starts selected.
    item.setAttribute('aria-pressed', state && title && title === state.titleText ? 'true' : 'false');
    if (title) {
      item.setAttribute('aria-label', title);
      // Keep the title for lookups, but off the wrapper: the native tooltip would otherwise
      // show up on top of the theme's own hover badge (see product-grid.css).
      item.dataset.siblingTitle = title;
      const wrap = item.querySelector('.gm_swatchwrap');
      if (wrap) wrap.removeAttribute('title');
    }
  }

  function onSelect(item) {
    const card = item.closest('.product-card');
    if (card) select(card, item);
  }

  // Hover and focus only warm the cache, so the click has its price and link ready.
  function warm(event) {
    const item = event.target.closest(ITEM);
    if (!item) return;
    enhance(item);
    lookup(titleOf(item));
  }

  document.addEventListener('pointerover', warm);
  document.addEventListener('focusin', warm);

  document.addEventListener('click', (event) => {
    const item = event.target.closest(ITEM);
    if (item) {
      event.preventDefault();
      onSelect(item);
      return;
    }
    // The "+N more" pill: the full selector lives on the product page of whatever is selected.
    const pill = event.target.closest(PILL);
    if (pill) {
      const card = pill.closest('.product-card');
      const link = card && card.querySelector('.product-card-title');
      if (link && link.href) window.location.assign(link.href);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const item = event.target.closest(ITEM);
    if (!item) return;
    event.preventDefault();
    onSelect(item);
  });

  // Keyboard users never hover, so expose what is already on the page up front…
  document.querySelectorAll(ITEM).forEach(enhance);
  // …and whatever infinite scroll / load-more appends later.
  new MutationObserver(() => document.querySelectorAll(ITEM).forEach(enhance))
    .observe(document.body, { childList: true, subtree: true });
})();
